export type RetryOptions = {
  maxRetries?: number
  initialDelay?: number
  maxDelay?: number
  backoffMultiplier?: number
  signal?: AbortSignal
  onRetry?: (attempt: number, error: Error) => void
}

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
    signal,
  } = options

  let lastError: Error
  let delay = initialDelay

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Check if aborted before attempting
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }

      return await fn()
    } catch (error) {
      lastError = error as Error

      // Don't retry on abort
      if (error instanceof Error && error.name === 'AbortError') {
        throw error
      }

      // Don't retry on client errors (4xx)
      if (error instanceof Error && error.message.includes('API Error: 4')) {
        throw error
      }

      // If this was the last attempt, throw
      if (attempt === maxRetries) {
        throw error
      }

      // Notify about retry
      if (options.onRetry) {
        options.onRetry(attempt + 1, lastError)
      }

      // Wait before retrying with exponential backoff
      await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(resolve, delay)

        // Allow cancellation during delay
        if (signal) {
          const abortHandler = () => {
            clearTimeout(timeoutId)
            reject(new DOMException('Aborted', 'AbortError'))
          }
          signal.addEventListener('abort', abortHandler, { once: true })
        }
      })

      // Increase delay for next attempt (with max limit)
      delay = Math.min(delay * backoffMultiplier, maxDelay)

      console.log(`Retrying save attempt ${attempt + 1}/${maxRetries} after ${delay}ms...`)
    }
  }

  throw lastError!
}
